import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';

import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import NotificationCenter from '../../components/notifications/NotificationCenter';

import {
  LayoutDashboard, Users, TrendingUp, MessageSquare,
  Settings, UserCog, LogOut, Menu, X, ChevronRight,
  Bell, Search, FileText, ClipboardList, Rocket, Mail
} from 'lucide-react';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { hasPermission, isSuperAdmin, hasSectionAccess } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Menu items with permission section mapping
  const menuItems = [
    { path: '/admin-panel', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'admin', 'manager'], permSection: null },
    { path: '/admin-panel/clients', icon: Users, label: 'Клиенты', roles: ['super_admin', 'admin', 'manager'], permSection: 'clients' },
    { path: '/admin-panel/requests', icon: FileText, label: 'Заявки', roles: ['super_admin', 'admin', 'manager'], permSection: 'requests' },
    { path: '/admin-panel/positions', icon: TrendingUp, label: 'Позиции', roles: ['super_admin', 'admin', 'manager'], permSection: 'positions' },
    { path: '/admin-panel/support', icon: MessageSquare, label: 'Поддержка', roles: ['super_admin', 'admin', 'manager'], permSection: 'support' },
    { path: '/admin-panel/team', icon: UserCog, label: 'Команда', roles: ['super_admin', 'admin'], permSection: 'team' },
    { path: '/admin-panel/audit-log', icon: ClipboardList, label: 'Аудит-лог', roles: ['super_admin'], permSection: null },
    { path: '/admin-panel/ido', icon: Rocket, label: 'Управление IDO', roles: ['super_admin', 'admin'], permSection: 'ido' },
    { path: '/admin-panel/email', icon: Mail, label: 'Email настройки', roles: ['super_admin'], permSection: null },
    { path: '/admin-panel/settings', icon: Settings, label: 'Настройки', roles: ['super_admin'], permSection: null },
    // Support role specific
    { path: '/admin-panel/support-dashboard', icon: LayoutDashboard, label: 'Панель Support', roles: ['support'], permSection: null },
    { path: '/admin-panel/new-clients', icon: Users, label: 'Новые клиенты', roles: ['support'], permSection: null },
    { path: '/admin-panel/support-tickets', icon: MessageSquare, label: 'Тикеты', roles: ['support'], permSection: null },
  ];

  // Filter menu items by role AND granular permissions
  const filteredMenuItems = menuItems.filter(item => {
    // First check role
    if (!item.roles.includes(user?.role)) return false;
    
    // Super admin bypasses permission checks
    if (isSuperAdmin) return true;
    
    // If no permission section defined, show the item (role check passed)
    if (!item.permSection) return true;
    
    // Check if user has 'view' permission for the section
    return hasPermission(item.permSection, 'view');
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: COLORS.background }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarOpen ? '260px' : '0px',
        minWidth: sidebarOpen ? '260px' : '0px',
        backgroundColor: COLORS.card,
        borderRight: `1px solid ${COLORS.border}`,
        transition: 'all 0.3s',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: isMobile ? 'fixed' : 'relative',
        zIndex: 100,
        height: '100vh',
      }}>
        {/* Logo */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Link to="/admin-panel" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: COLORS.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '800',
              fontSize: '16px',
              color: '#000',
            }}>EX</div>
            <span style={{ color: COLORS.text, fontWeight: '700', fontSize: '18px' }}>Admin</span>
          </Link>
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
              <X size={20} />
            </button>
          )}
        </div>

        {/* Menu */}
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => isMobile && setSidebarOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  marginBottom: '4px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  color: isActive ? COLORS.primary : COLORS.textSecondary,
                  backgroundColor: isActive ? `${COLORS.primary}15` : 'transparent',
                  fontWeight: isActive ? '600' : '500',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                }}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: COLORS.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000',
              fontWeight: '700',
              fontSize: '14px',
            }}>
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email}
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px', textTransform: 'capitalize' }}>
                {user?.role?.replace('_', ' ')}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: `${COLORS.danger}15`,
              border: `1px solid ${COLORS.danger}40`,
              borderRadius: '8px',
              color: COLORS.danger,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <LogOut size={18} />
            Выйти
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top Bar */}
        <header style={{
          height: '64px',
          backgroundColor: COLORS.card,
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer', padding: '8px' }}
            >
              <Menu size={24} />
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: COLORS.background,
              borderRadius: '8px',
              padding: '8px 16px',
              width: isMobile ? '200px' : '300px',
            }}>
              <Search size={18} color={COLORS.textSecondary} />
              <input
                type="text"
                placeholder="Поиск клиентов..."
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'none',
                  color: COLORS.text,
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <NotificationCenter />
          </div>
        </header>

        {/* Page Content */}
        <main style={{ flex: 1, padding: isMobile ? '16px' : '24px', overflowY: 'auto' }}>
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 99,
          }}
        />
      )}
    </div>
  );
};

export default AdminLayout;
